// ── THE ARC-IC SCREENER — one farm, one state, and what it is allowed to claim ────────────
//
// Kevin, 2026-10-07: *"I think a screener by farm is a good tool, dont need you to build it
// for the whole book. Something where we can pull up a farm and run it to see how it looks.
// A customized run."*
//
// ⚠⚠ THE POOL IS THE WHOLE RISK IN THIS TOOL, AND IT IS NOT OURS TO SEE.
// ARC-IC pools every covered commodity planted on ALL of a producer's ARC-IC farms in ONE
// STATE into a single revenue figure. What this app can see is the crops it INSURES. Those
// are different sets, and the difference is not symmetric:
//
//   · a pool NARROWER than FSA's overstates the payment whenever the crop we cannot see did
//     better than the ones we can — the good crop that would have offset the bad one is
//     simply absent, so the shortfall survives;
//   · it understates it in the opposite case.
//
// So this module NEVER presents its pool as the farm's ARC-IC pool. It presents it as "the
// crops Silas can see", names what that is, and asks the agent to confirm the farm grows
// nothing else in the state. `poolConfirmed` is a typed answer, it has no default, and until
// it is given the result carries `provisional: true`. A screener that quietly assumed its
// own book was the whole farm would be the invented-explanation failure this repo opens
// with, applied to a number a grower takes to an FSA office.
//
// ⚠ AND IT IS A SCREENER, NOT A CALCULATOR. Its job is "is ARC-IC worth taking to the FSA
// office for this farm before 11 December", on inputs that are partly ESTIMATED. Every
// figure carries its own provenance and every surface prints it. It computes no election,
// recommends nothing, and where an input is missing it says so rather than substituting a
// plausible one.
//
// It calculates nothing of its own: the arithmetic is ./arcIcRule's and the ARC-CO side is
// ./arcPlcElection's countyRead — the SAME call the Coverage Advisor and the one-page
// election summary make, so the three surfaces cannot price one county three ways.
import {
  arcIcFarmRevenue, arcIcPayment, arcIcYieldThresholds, arcIcVsCo, olympicAverage,
  ARC_IC_BASE_ACRE_FACTOR, OLYMPIC_YEARS,
} from "./arcIcRule.js";
import { BASE_ACRE_FACTOR } from "./arcPlcRule.js";

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();

// ── PROVENANCE, AND IT IS RANKED ──────────────────────────────────────────────────────────
// Every input to the pool arrives one of four ways, and a surface that cannot tell them
// apart is a surface that presents an estimate as a figure. `typed` always outranks a
// derivation — the grower's own FSA record beats anything we can compute from insurance
// data.
export const INPUT_SOURCES = {
  typed: { rank: 1, label: "entered", estimate: false },
  // ⚠ APH IS NOT AN FSA YIELD HISTORY. RMA's approved yield is built on RMA's own rules —
  // T-yields, yield substitution, cups and floors, APH exclusions — and FSA's ARC-IC
  // benchmark is built on the farm's actual yields under FSA's. They are routinely close
  // and they are not the same number, so an APH-derived benchmark is an ESTIMATE on every
  // surface that prints it, never "the farm's benchmark yield".
  aph_olympic: { rank: 2, label: "estimated from APH history", estimate: true },
  reported: { rank: 2, label: "from reported production", estimate: false },
  insured_acres: { rank: 3, label: "insured acres less prevent plant", estimate: true },
  missing: { rank: 9, label: "not available", estimate: false },
};

// ⚠ TRANSITIONAL AND T-YIELDS ARE EXCLUDED, NOT AVERAGED IN. A `T`/`I`/`TA` row is RMA's
// substitute for a year the farm has no record of; folding it into a figure labelled "the
// farm's own history" states a yield the farm never grew. `Z` is zero planted acres and is
// not a yield at all. `PP`/`PW` are prevent plant — no bushel came off those acres.
export const ACTUAL_RECORD_TYPES = new Set(["A", "PA"]);

// ⚠ FSA'S COVERED-COMMODITY YIELD IS IN BUSHELS, AND CORN IS TWO PRODUCTS IN THIS TABLE.
// `production_history` carries corn GSG grain in bu/ac beside corn GSS silage in ton/ac
// under the one crop name, and this repo already records what averaging them produces:
// Lismore's Steuben corn read 86.5 bu/ac — the mean of 154 bu and 18.9 ton — on $2.28M of
// liability. An ARC-IC benchmark built that way would be a yield on neither crop, priced
// against FSA's bushel benchmark price. So the unit is PICKED, never blended, and what was
// set aside is named.
const BUSHEL_UNITS = new Set(["BU", "BU.", "BUSHEL", "BUSHELS", "BU/AC", "BU/ACRE"]);
const unitOf = (h) => up(h && (h.unit_of_measure || h.yield_unit));

/**
 * benchmarkYieldFor({ crop, typed, history }) -> { value, source, years, why, excluded }
 *
 * The farm's own benchmark yield, which is the single biggest determinant of the answer.
 * A typed figure wins outright. Failing that, an Olympic average of the farm's APH ACTUAL
 * years — and only the actual ones, in one unit of measure. Below OLYMPIC_YEARS of them the
 * answer is MISSING, not a plain mean of three: that is a different statistic and printing
 * it under this name would misstate the benchmark the whole payment rests on.
 *
 * `history` rows are `production_history`: { crop_year, yield_per_acre, record_type,
 * unit_of_measure }.
 */
export function benchmarkYieldFor({ crop, typed = null, history = [] } = {}) {
  const t = num(typed);
  if (t != null && t > 0)
    return { crop: up(crop), value: t, source: "typed", years: null, yearsUsed: [], why: null, excluded: null };
  const actual = (history || [])
    .filter((h) => ACTUAL_RECORD_TYPES.has(up(h && h.record_type)))
    .map((h) => ({ year: num(h && h.crop_year), yield: num(h && h.yield_per_acre), unit: unitOf(h) }))
    .filter((h) => h.yield != null && h.yield > 0);

  // One unit, chosen rather than blended: bushels where any row says so, otherwise whatever
  // unit the most rows carry. An unstated unit rides with the chosen one — a blank is not
  // evidence of a different unit, and dropping those would hide most of the history, since
  // the column is thinly populated.
  const byUnit = {};
  for (const h of actual) (byUnit[h.unit || ""] = byUnit[h.unit || ""] || []).push(h);
  const named = Object.keys(byUnit).filter(Boolean);
  let pick = null;
  if (named.length > 1) pick = named.find((u) => BUSHEL_UNITS.has(u)) || named.sort((a, b) => byUnit[b].length - byUnit[a].length)[0];
  else if (named.length === 1) pick = named[0];
  const kept = pick == null ? actual : actual.filter((h) => h.unit === pick || !h.unit);
  const excluded = pick == null ? [] : actual.filter((h) => h.unit && h.unit !== pick);

  const oly = olympicAverage(kept.map((h) => h.yield));
  const exc = excluded.length
    ? { unit: [...new Set(excluded.map((h) => h.unit))].join(", "), years: excluded.length,
        why: "a different unit of measure — averaging it with " + pick + " would state a yield on neither crop" }
    : null;
  if (oly == null)
    return {
      crop: up(crop), value: null, source: "missing", years: kept.length, yearsUsed: [],
      why: kept.length + " actual APH year" + (kept.length === 1 ? "" : "s")
        + (pick ? " in " + pick : "") + " on file; an Olympic average needs " + OLYMPIC_YEARS,
      excluded: exc,
    };
  return {
    crop: up(crop), value: oly, source: "aph_olympic", years: kept.length, unit: pick || null,
    yearsUsed: kept.map((h) => h.year).filter((y) => y != null).sort((a, b) => a - b),
    why: null, excluded: exc,
  };
}

// ── THE FARM'S CANDIDATE POOLS — one per STATE, from the farm's own insurance ─────────────
//
// ⚠ EVERY DEPENDENCY IS INJECTED, and that is deliberate twice over. `fetchRows` is the
// caller's REST read (the app's sbFetch, or a service-role fetch in a tool) so the page an
// agent reads and the sheet a tool prints travel the same executed path — the pattern
// arcPlcFarmSummary already uses. `countyReadFn` is ./arcPlcElection's `countyRead`, passed
// in rather than imported, so the ARC-CO side of the comparison is the SAME call the
// Coverage Advisor and the one-page election summary make and this file stays loadable by
// node's ESM loader for its own checker.
export const PH_SELECT = "farmer_id,crop,crop_year,yield_per_acre,record_type,unit_of_measure";
export const COV_SELECT = "policy_id,crop,county,acres,pp_acres";

// ⚠ FSA's ARC-IC BENCHMARK IS BUILT ON FIVE YEARS, and `production_history` holds more than
// that — so the window is stated rather than "whatever is in the table". Ten years back of
// the program year gives the Olympic average five ACTUAL years to find after T-yields,
// prevent plant and zero-planted years are excluded, without reaching into a rotation the
// farm no longer runs.
export const APH_LOOKBACK_YEARS = 10;

export async function loadArcIcCandidates(fetchRows, { farmerIds = [], programYear = null } = {}) {
  const Y = Number(programYear);
  if (!Number.isInteger(Y) || Y < 2014 || Y > 2100) throw new Error("programYear is required (got " + JSON.stringify(programYear) + ")");
  // ⚠ A FARM IS NOT ALWAYS ONE `farmers` ROW — the CY2027 rollover minted five for one
  // grower when the file spelled an insured differently. Taking the first id would be right
  // today and silently screen half a farm the next time it is not.
  const ids = (farmerIds || []).filter((v) => v != null);
  if (!ids.length) throw new Error("no farm chosen");
  const inList = ids.length === 1 ? `farmer_id=eq.${ids[0]}` : `farmer_id=in.(${ids.join(",")})`;

  const pol = (await fetchRows("policies", { select: "id,state,crop_year", filters: [inList, `crop_year=eq.${Y}`] })) || [];
  const stBy = {};
  for (const p of pol) stBy[p.id] = up(p.state);
  const polIds = Object.keys(stBy);
  const cov = polIds.length
    ? (await fetchRows("crop_coverage", { select: COV_SELECT, filters: [`policy_id=in.(${polIds.join(",")})`] })) || []
    : [];
  const refRows = (await fetchRows("arc_plc_reference", { filters: [`program_year=eq.${Y}`] })) || [];
  const refs = {};
  for (const r of refRows) if (r && Number(r.program_year) === Y && r.crop) refs[up(r.crop)] = r;
  const ph = (await fetchRows("production_history", {
    select: PH_SELECT,
    filters: [inList, `crop_year=gte.${Y - APH_LOOKBACK_YEARS}`, `crop_year=lt.${Y}`, "record_type=in.(A,PA)"],
  })) || [];

  // ⚠ PLANTED, NOT INSURED. `crop_coverage.acres` INCLUDES prevent plant and ARC-IC pools
  // PLANTED acres — no bushel came off a prevent-planted acre, so counting it both dilutes
  // the revenue per acre and inflates the pool. Same rule the production-report panel
  // follows, and the figure is still labelled an estimate because it is insured acreage,
  // not FSA's reported planting.
  const byState = {};
  const noState = [], notCovered = new Map();
  for (const c of cov) {
    const crop = up(c.crop), st = stBy[c.policy_id] || "";
    const planted = Math.max(0, (num(c.acres) || 0) - Math.min(num(c.acres) || 0, Math.max(0, num(c.pp_acres) || 0)));
    if (!crop || planted <= 0) continue;
    // A coverage line whose policy records no state cannot be pooled — ARC-IC's pool is
    // state-wide and the same county name exists in several states. Named, never guessed.
    if (!st) { noState.push({ crop, county: up(c.county) }); continue; }
    if (!refs[crop]) { notCovered.set(crop, (notCovered.get(crop) || 0) + planted); continue; }
    const s = (byState[st] = byState[st] || { state: st, crops: {}, counties: [] });
    const e = (s.crops[crop] = s.crops[crop] || { crop, plantedAcres: 0, counties: {} });
    e.plantedAcres += planted;
    e.counties[up(c.county)] = (e.counties[up(c.county)] || 0) + planted;
    s.counties.push({ crop, county: up(c.county), acres: planted });
  }

  const states = Object.keys(byState).sort().map((st) => {
    const s = byState[st];
    return {
      state: st,
      counties: s.counties,
      crops: Object.values(s.crops).sort((a, b) => b.plantedAcres - a.plantedAcres).map((e) => ({
        crop: e.crop,
        plantedAcres: +e.plantedAcres.toFixed(1),
        plantedSource: "insured_acres",
        counties: Object.entries(e.counties).map(([county, acres]) => ({ county, acres: +acres.toFixed(1) }))
          .sort((a, b) => b.acres - a.acres),
        benchmarkPrice: num(refs[e.crop] && refs[e.crop].arc_benchmark_price),
        myaPrice: num(refs[e.crop] && refs[e.crop].mya_price),
        benchmark: benchmarkYieldFor({ crop: e.crop, history: ph.filter((h) => up(h.crop) === e.crop) }),
        // ⚠ NOTHING SUPPLIES THE 2026 YIELD. It is TYPED. Our own harvest data is machine
        // acreage over whatever the monitor measured, not the farm's FSA-reported production
        // per covered commodity, and the two differ by exactly the acres a monitor missed.
        actualYield: null, actualSource: "missing",
      })),
    };
  });

  return {
    programYear: Y, states,
    refs,
    // Every reason a crop or a line is not in a pool, so the screen can say so rather than
    // quietly screening a narrower farm than the one on the policy.
    notCovered: [...notCovered.entries()].map(([crop, acres]) => ({ crop, acres: +acres.toFixed(1),
      reason: "no FSA ARC/PLC figures are loaded for this crop" })).sort((a, b) => a.crop.localeCompare(b.crop)),
    noState,
    aphRows: ph.length,
  };
}

/**
 * buildArcIcPool({ crops, state, programYear }) -> the pool, its provenance and its refusals.
 *
 * `crops` is one entry per covered commodity the farm grows IN ONE STATE:
 *   { crop, state, plantedAcres, plantedSource, benchmark, actualYield, actualSource,
 *     benchmarkPrice, myaPrice }
 *
 * ⚠ ONE STATE. ARC-IC's pool is state-wide and does not cross a state line, so a farm with
 * ground in two states has TWO pools and two elections. This module REFUSES a mixed-state
 * call rather than pooling across one, because a pooled two-state revenue is a number FSA
 * would never compute and it looks exactly like a correct one.
 */
export function buildArcIcPool({ crops = [], state = null, programYear = null } = {}) {
  const st = up(state);
  if (!st) return { ok: false, why: "no state — ARC-IC pools state-wide, so the pool needs one" };
  const others = [...new Set((crops || []).map((c) => up(c && c.state)).filter(Boolean))].filter((s) => s !== st);
  if (others.length)
    return {
      ok: false,
      why: "this pool was handed " + (others.length + 1) + " states (" + [st, ...others].join(", ")
        + "). ARC-IC is elected and pooled per state, so each one is its own screener run.",
    };

  const entries = [], estimated = [], unusable = [];
  for (const c of crops || []) {
    const crop = up(c && c.crop);
    if (!crop) continue;
    const bench = (c && c.benchmark) || { value: null, source: "missing", why: "no benchmark yield resolved" };
    const ac = num(c && c.plantedAcres), ay = num(c && c.actualYield);
    const bp = num(c && c.benchmarkPrice), mp = num(c && c.myaPrice);
    const e = {
      crop, plantedAcres: ac, plantedSource: (c && c.plantedSource) || "missing",
      benchmarkYield: num(bench.value), benchmarkSource: bench.source || "missing",
      benchmarkYears: bench.years != null ? bench.years : null,
      benchmarkYearsUsed: bench.yearsUsed || null, benchmarkWhy: bench.why || null,
      actualYield: ay, actualSource: (c && c.actualSource) || "missing",
      benchmarkPrice: bp, myaPrice: mp,
    };
    entries.push(e);
    if ([e.benchmarkSource, e.actualSource, e.plantedSource].some((s) => (INPUT_SOURCES[s] || {}).estimate))
      estimated.push(crop);
    if (e.benchmarkYield == null || e.actualYield == null || ac == null || ac <= 0 || bp == null || mp == null)
      unusable.push(crop);
  }

  const rev = arcIcFarmRevenue({ crops: entries.map((e) => ({
    crop: e.crop, plantedAcres: e.plantedAcres, benchmarkYield: e.benchmarkYield,
    actualYield: e.actualYield, benchmarkPrice: e.benchmarkPrice, myaPrice: e.myaPrice,
  })) });

  return {
    ok: rev.ok, why: rev.ok ? null : rev.why,
    state: st, programYear: programYear != null ? Number(programYear) : null,
    entries, estimated: [...new Set(estimated)], unusable: [...new Set(unusable)],
    revenue: rev.ok
      ? { benchmarkPerAcre: rev.benchmarkPerAcre, actualPerAcre: rev.actualPerAcre, plantedAcres: rev.plantedAcres }
      : null,
    missing: rev.missing || [],
    used: rev.used || [],
  };
}

/**
 * arcCoComparison({ reads }) -> the farm's ARC-CO rate per acre, acre-weighted.
 *
 * ⚠ ARC-CO IS PER CROP PER COUNTY AND ARC-IC IS ONE NUMBER FOR THE FARM, so the two are not
 * comparable until the ARC-CO side is rolled up — and the roll-up should be weighted by the
 * BASE acres ARC-CO would actually pay on. Nothing in this database holds FSA base acres per
 * county, so this weights by PLANTED acres and SAYS SO: that is the right weight only where
 * a county's base-acre mix matches its planted mix, which is exactly what a farm considering
 * ARC-IC on a reshuffled rotation does not have.
 *
 * `reads` is one entry per (crop, county): { crop, county, acres, arcPerAcre } — `arcPerAcre`
 * straight off countyRead.
 */
export function arcCoComparison({ reads = [] } = {}) {
  let wSum = 0, acres = 0;
  const priced = [], unpriced = [];
  for (const r of reads || []) {
    const ac = num(r && r.acres), v = num(r && r.arcPerAcre);
    const cell = { crop: up(r && r.crop), county: up(r && r.county) };
    if (ac == null || ac <= 0) { unpriced.push({ ...cell, why: "no acreage" }); continue; }
    if (v == null) { unpriced.push({ ...cell, why: "no FSA county figures" }); continue; }
    wSum += v * ac; acres += ac;
    priced.push({ ...cell, acres: ac, arcPerAcre: v });
  }
  if (acres <= 0) return { ok: false, why: "no crop/county cell carries both an acreage and FSA figures", unpriced };
  // ⚠ `arcPerAcre` HAS ALREADY HAD ARC-CO's 85% APPLIED, so it is divided back out to give
  // the RATE — which is what arcIcVsCo takes. Handing it two already-factored numbers would
  // apply 0.85 and 0.65 a second time and understate both sides by a third.
  return {
    ok: true,
    ratePerAcre: (wSum / acres) / BASE_ACRE_FACTOR,
    perBaseAcre: wSum / acres,
    acres, priced, unpriced,
    weightBasis: "planted acres — no FSA base-acre split per county exists in this database",
  };
}

/**
 * screenArcIc({ pool, baseAcres, arcCo, poolConfirmed }) -> the screener's whole answer.
 *
 * ⚠ `poolConfirmed` HAS NO DEFAULT AND THE RESULT IS PROVISIONAL WITHOUT IT. See the header:
 * the pool we can see may be narrower than FSA's, and a narrower pool overstates the payment
 * whenever the invisible crop did better. Defaulting it to true would turn the one caveat
 * that matters into a comment nobody reads.
 */
export function screenArcIc({ pool = null, baseAcres = null, arcCo = null, poolConfirmed = null } = {}) {
  if (!pool || !pool.ok) return { ok: false, why: (pool && pool.why) || "no pool", stage: "pool" };
  const pay = arcIcPayment({
    benchmarkPerAcre: pool.revenue.benchmarkPerAcre,
    actualPerAcre: pool.revenue.actualPerAcre,
    baseAcres,
  });
  if (!pay.ok) return { ok: false, why: pay.why, stage: "payment", pool };

  const vs = arcCo && arcCo.ok
    ? arcIcVsCo({ arcIcRatePerAcre: pay.ratePerAcre, arcCoRatePerAcre: arcCo.ratePerAcre })
    : { ok: false, why: (arcCo && arcCo.why) || "no ARC-CO comparison available" };

  // Single-crop pools get the yield thresholds; a pool of several has no single answer, and
  // saying so is more useful than picking one crop's line and labelling it the farm's.
  const single = pool.used.length === 1 ? pool.entries.find((e) => e.crop === pool.used[0].crop) : null;
  const thresholds = single
    ? arcIcYieldThresholds({ benchmarkPrice: single.benchmarkPrice, myaPrice: single.myaPrice })
    : null;

  // ⚠ WHAT MAKES THE ANSWER PROVISIONAL, LISTED RATHER THAN SUMMARISED. Each is a different
  // reason to go and check something, and collapsing them into one badge loses the only
  // actionable part.
  const caveats = [];
  if (poolConfirmed !== true)
    caveats.push({ key: "pool_unconfirmed", severity: "high",
      text: "the pool is the crops Silas insures in " + pool.state
        + " — ARC-IC pools ALL covered commodities on the producer's ARC-IC farms in the state."
        + " If this farm grows anything else there, this payment is overstated." });
  if (pool.unusable.length)
    caveats.push({ key: "crop_excluded", severity: "high",
      text: pool.unusable.join(", ") + " could not enter the pool, so "
        + (pool.unusable.length === 1 ? "it is" : "they are") + " missing from the revenue on both sides." });
  if (pool.estimated.length)
    caveats.push({ key: "estimated_input", severity: "medium",
      text: "an input for " + pool.estimated.join(", ") + " is estimated rather than the farm's own FSA figure." });
  if (arcCo && arcCo.ok && arcCo.unpriced.length)
    caveats.push({ key: "arcco_partial", severity: "medium",
      text: arcCo.unpriced.length + " crop/county cell" + (arcCo.unpriced.length === 1 ? "" : "s")
        + " carry no ARC-CO figure, so the comparison covers part of the farm." });

  return {
    ok: true,
    provisional: caveats.some((c) => c.severity === "high"),
    state: pool.state, programYear: pool.programYear,
    pool, payment: pay, arcCo: arcCo || null, comparison: vs, thresholds,
    paidAcreFactor: ARC_IC_BASE_ACRE_FACTOR,
    caveats,
  };
}
